import { ClockIcon } from '@heroicons/react/24/outline';
import { Button } from '@leadsales/design-system/react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoadedFeatureFlag } from 'src/hooks/featureFlags/useLoadedFeatureFlag.hook';
import useProgrammedMessageAction from 'src/components/Chat/components/MessageBar/components/ProgrammedMessageAction/ProgrammedMessageAction.hook';
import { useScheduledDateFormat } from 'src/components/Chat/components/MessageContainer/components/EventBubble/components/FollowUpBubble/FollowUpBubble.hook';
import { useSchedulerName } from 'src/components/Chat/components/MessageContainer/components/EventBubble/components/ScheduledLegend/ScheduledLegend.hook';
import { findScheduledCard } from './UpcomingSendsBar.functions';
import type { tPendingFollowUp } from './UpcomingSendsBar.types';
import useUpcomingSends from './useUpcomingSends.hook';
import './UpcomingSendsBar.styles.scss';

type tUpcomingSendRowProps = {
  item: tPendingFollowUp;
  formatDate: (date: string) => string;
};

function UpcomingSendRow(props: tUpcomingSendRowProps) {
  const { item, formatDate } = props;
  const schedulerName = useSchedulerName(item.createdBy);

  return (
    <li className="UpcomingSendsBar__Item" data-testid="upcoming-sends-item">
      <span className="UpcomingSendsBar__ItemDate">
        {formatDate(item.triggerDate)}
      </span>
      {/* Session replay records attributes unmasked; the block class keeps
          the message in `title` out of it. */}
      <span
        className="UpcomingSendsBar__ItemText ph-no-capture"
        title={item.text}
      >
        {item.text}
      </span>
      {schedulerName ? (
        <span className="UpcomingSendsBar__ItemBy">{schedulerName}</span>
      ) : null}
    </li>
  );
}

function UpcomingSendsBarContent() {
  const { t } = useTranslation('chat/chat');
  const { pending, refresh } = useUpcomingSends();
  const { deleteFollowUp, loading } = useProgrammedMessageAction();
  const formatDate = useScheduledDateFormat();
  const [isListOpen, setIsListOpen] = useState(false);
  const titleId = useId();
  const listId = useId();

  const next: tPendingFollowUp | undefined = pending[0];

  useEffect(() => {
    if (!next) setIsListOpen(false);
  }, [next]);

  if (!next) return null;

  // "Ver": the card of the next send when the thread has it on screen;
  // otherwise the pending sends as a short list, instead of paging the whole
  // history to reach one card.
  function showNext() {
    const card = next ? findScheduledCard(next.chatEventId) : null;
    if (card) {
      setIsListOpen(false);
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setIsListOpen((open) => !open);
  }

  // The same cancel the card's "Cancelar envío" runs (a WhatsApp API template
  // cancels its campaign first, BUG-687), for the send whose date the bar
  // shows. The card has no confirmation step, so the bar has none either.
  async function cancelNext() {
    if (!next) return;
    await deleteFollowUp(next.chatEventId, {
      knownFollowUp: {
        isWapiTemplate: next.isWapiTemplate,
        campaignId: next.campaignId,
      },
    });
    refresh();
  }

  return (
    <section
      className="UpcomingSendsBar"
      aria-labelledby={titleId}
      data-testid="upcoming-sends-bar"
    >
      {isListOpen ? (
        <div className="UpcomingSendsBar__List" id={listId}>
          <div className="UpcomingSendsBar__ListHeader">
            <p className="UpcomingSendsBar__ListTitle">
              {t('upcomingSends.listTitle')}
            </p>
            <Button
              type="button"
              color="ghost"
              size="xs"
              onClick={() => setIsListOpen(false)}
            >
              {t('upcomingSends.closeList')}
            </Button>
          </div>
          <ul className="UpcomingSendsBar__Items">
            {pending.map((item) => (
              <UpcomingSendRow
                key={item.chatEventId}
                item={item}
                formatDate={formatDate}
              />
            ))}
          </ul>
        </div>
      ) : null}
      <div className="UpcomingSendsBar__Row">
        <ClockIcon className="UpcomingSendsBar__Icon" aria-hidden="true" />
        <p className="UpcomingSendsBar__Summary">
          <strong id={titleId}>
            {t('upcomingSends.title', { total: pending.length })}
          </strong>
          <span className="UpcomingSendsBar__NextDate">
            {' · '}
            {formatDate(next.triggerDate)}
          </span>
        </p>
        <div className="UpcomingSendsBar__Actions">
          <Button
            type="button"
            color="ghost"
            size="xs"
            onClick={showNext}
            aria-expanded={isListOpen}
            aria-controls={isListOpen ? listId : undefined}
          >
            {t('upcomingSends.view')}
          </Button>
          <Button
            type="button"
            color="ghost"
            size="xs"
            onClick={cancelNext}
            disabled={loading}
            aria-busy={loading}
          >
            {t('scheduledMessage.cancelSend')}
          </Button>
        </div>
      </div>
    </section>
  );
}

/**
 * INT-1500 U12 (D8): "Próximos envíos (N) · {fecha}" pinned above the
 * composer, so a scheduled message stays in sight after the conversation
 * scrolls past its card. Hidden while nothing is pending, and entirely absent
 * (no request, no subscription) while `chat_upcoming-sends-bar` is off.
 */
export default function UpcomingSendsBar() {
  const isEnabled = useLoadedFeatureFlag('chat_upcoming-sends-bar');

  return isEnabled ? <UpcomingSendsBarContent /> : null;
}
